import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  Grid,
  Divider,
  IconButton,
  alpha,
} from '@mui/material';
import {
  Keyboard,
  X,
  Command,
  Navigation,
  Search,
  FileText,
  Zap,
  Bot,
  Settings,
} from 'lucide-react';

const shortcutCategories = [
  {
    name: 'Navigation',
    icon: Navigation,
    color: '#6366F1',
    shortcuts: [
      { keys: ['⌘', 'K'], description: 'Open Command Palette' },
      { keys: ['⌘', '/'], description: 'Global Search' },
      { keys: ['⌘', '1'], description: 'Go to Dashboard' },
      { keys: ['⌘', '2'], description: 'Go to Jobs' },
      { keys: ['⌘', '3'], description: 'Go to Clients' },
      { keys: ['⌘', '4'], description: 'Go to Estimates' },
      { keys: ['Esc'], description: 'Close Dialog/Modal' },
    ],
  },
  {
    name: 'Actions',
    icon: Zap,
    color: '#10B981',
    shortcuts: [
      { keys: ['⌘', 'N'], description: 'Create New Item' },
      { keys: ['⌘', 'S'], description: 'Save Current' },
      { keys: ['⌘', 'E'], description: 'Edit Selected' },
      { keys: ['⌘', 'D'], description: 'Duplicate Selected' },
      { keys: ['Delete'], description: 'Delete Selected' },
      { keys: ['⌘', 'Z'], description: 'Undo' },
      { keys: ['⌘', '⇧', 'Z'], description: 'Redo' },
    ],
  },
  {
    name: 'AI Tools',
    icon: Bot,
    color: '#8B5CF6',
    shortcuts: [
      { keys: ['⌘', 'J'], description: 'Open AI Chat' },
      { keys: ['⌘', '⇧', 'A'], description: 'AI Assistant' },
      { keys: ['⌘', '⇧', 'E'], description: 'AI Estimate Helper' },
      { keys: ['⌘', '⇧', 'I'], description: 'AI Inventory Assistant' },
    ],
  },
  {
    name: 'Views & Filters',
    icon: Search,
    color: '#F59E0B',
    shortcuts: [
      { keys: ['⌘', 'F'], description: 'Filter Current View' },
      { keys: ['⌘', '⇧', 'F'], description: 'Advanced Search' },
      { keys: ['⌘', 'L'], description: 'Toggle List/Grid View' },
      { keys: ['⌘', '⇧', 'S'], description: 'Toggle Sidebar' },
      { keys: ['⌘', '+'], description: 'Zoom In' },
      { keys: ['⌘', '-'], description: 'Zoom Out' },
    ],
  },
  {
    name: 'Documents',
    icon: FileText,
    color: '#EF4444',
    shortcuts: [
      { keys: ['⌘', 'P'], description: 'Print Current View' },
      { keys: ['⌘', 'O'], description: 'Open Document' },
      { keys: ['⌘', '⇧', 'E'], description: 'Export to PDF' },
      { keys: ['⌘', '⇧', 'C'], description: 'Export to CSV' },
    ],
  },
  {
    name: 'Settings',
    icon: Settings,
    color: '#64748B',
    shortcuts: [
      { keys: ['⌘', ','], description: 'Open Settings' },
      { keys: ['⌘', '?'], description: 'Show Keyboard Shortcuts' },
      { keys: ['⌘', '⇧', 'N'], description: 'Enable Notifications' },
    ],
  },
];

function KeyboardShortcutsDialog({ open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          maxHeight: '85vh',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px -8px rgba(99, 102, 241, 0.4)',
            }}
          >
            <Keyboard size={24} color="#ffffff" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Keyboard Shortcuts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Master FusionPM with quick shortcuts
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {shortcutCategories.map((category, catIdx) => {
            const Icon = category.icon;
            return (
              <Grid item xs={12} md={6} key={catIdx}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: alpha(category.color, 0.3),
                      boxShadow: `0 4px 20px -4px ${alpha(category.color, 0.15)}`,
                    },
                  }}
                >
                  {/* Category Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        bgcolor: alpha(category.color, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon size={18} color={category.color} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {category.name}
                    </Typography>
                  </Box>

                  {/* Shortcuts */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {category.shortcuts.map((shortcut, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary', flex: 1 }}
                        >
                          {shortcut.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {shortcut.keys.map((key, keyIdx) => (
                            <Chip
                              key={keyIdx}
                              label={key}
                              size="small"
                              sx={{
                                height: 26,
                                minWidth: 26,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                bgcolor: alpha(category.color, 0.08),
                                color: category.color,
                                border: `1px solid ${alpha(category.color, 0.2)}`,
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Footer Tip */}
        <Box
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: 3,
            bgcolor: alpha('#6366F1', 0.04),
            border: '1px solid',
            borderColor: alpha('#6366F1', 0.1),
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: alpha('#6366F1', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Command size={20} color="#6366F1" />
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              Pro Tip
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Press <strong>⌘K</strong> (Mac) or <strong>Ctrl+K</strong> (Windows) anywhere to open the Command Palette for quick navigation and actions.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default KeyboardShortcutsDialog;
